import { PERSONAS, type MetaAudience, type MetaAudienceRef, type MetaAudienceTargeting, type Persona } from './types';

// Growth's audience setup, by audience name: ids differ between ad accounts, and names are what
// Growth manages in Ads Manager. Changes here come from Growth.

/** The audiences each persona targets. */
export const PERSONA_AUDIENCES: Record<Persona, readonly string[]> = {
  Sales: ['PROD_Sales_CW_AllTiers_Ads', 'PROD_Sales_CW_Tier1-2_Ads', 'Lookalike (1%) - PROD_Sales_CW_AllTiers_Ads'],
  RevOps: [
    '[Prod] RevOps Persona Tier 1-2, 50-5000 employee US companies',
    'Lookalike (1%) - [Prod] RevOps Persona Tier 1-2, 50-5000 employee US companies',
  ],
  Marketing: [
    '[Prod] Marketing Persona Tier 1-2, 50-5000 employee US companies',
    'Lookalike (1%) - [Prod] Marketing Persona Tier 1-2, 50-5000 employee US companies',
  ],
  // The OOF audience stands in until Growth builds a founders audience.
  Founders: ['OOF Meta Ads Audience (09/02)'],
};

/** Excluded from every ad unless a reviewer removes one, or a requester gives a reason. */
export const DEFAULT_EXCLUSIONS: readonly string[] = [
  'ClaySync_ClayEmployees',
  'ClaySync_Users_AfterSep2025',
  'ClaySync_Users_Between_Jun2024_Sep2025_new',
  'PRO_ENT_MAIN',
  'ts_cc_marketers_ent',
  'ts_inbound_form',
  'ts_marketers_cc_churned_50-5k_LAL',
];

/** Never offered. A saved request that still uses one keeps it. */
export const DEPRECATED_AUDIENCES: ReadonlySet<string> = new Set([
  'PROD_RevOpsMarOps_CW_AllTiers_Ads',
  'PROD_RevOpsMarOps_CW_Tier1-2_Ads',
  'Lookalike (1%) - PROD_RevOpsMarOps_CW_AllTiers_Ads',
  'Lookalike (CA, US, 1%) - PROD_Marketing_CW_Tier1-2_Ads',
]);

export function personaOf(audienceName: string): Persona | null {
  return PERSONAS.find(p => PERSONA_AUDIENCES[p].includes(audienceName)) ?? null;
}

/** The account's audiences with these names, in the given order, and the names it doesn't have. */
export function resolveByName(audiences: MetaAudience[], names: readonly string[]): { found: MetaAudienceRef[]; missing: string[] } {
  const byName = new Map(audiences.map(a => [a.name, a]));
  const found: MetaAudienceRef[] = [];
  const missing: string[] = [];
  for (const name of names) {
    const audience = byName.get(name);
    if (audience) found.push({ id: audience.id, name: audience.name });
    else missing.push(name);
  }
  return { found, missing };
}

/** Persona audiences first (in persona order), then the rest by name, with deprecated ones left out. */
export function offeredAudiences(audiences: MetaAudience[]): MetaAudience[] {
  const rank = (a: MetaAudience) => {
    const persona = personaOf(a.name);
    return persona ? PERSONAS.indexOf(persona) : PERSONAS.length;
  };
  return audiences
    .filter(a => !DEPRECATED_AUDIENCES.has(a.name))
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

/** The exclusions a requester removed from the defaults, by name. */
export function droppedDefaults(excludedNames: readonly string[], availableNames: ReadonlySet<string> | null = null): string[] {
  return DEFAULT_EXCLUSIONS.filter(name => !excludedNames.includes(name) && (availableNames === null || availableNames.has(name)));
}

/**
 * A request's targeting as saved. A dropped default exclusion keeps its reason; reasons for anything
 * else are dropped. For a requester (not a reviewer), every default exclusion in the account must
 * stay or have a reason, and exclusions a reviewer added (any non-default in `stored`) can't be
 * removed. `availableNames` is the account's audience names, or null when unknown.
 */
export function allowedTargeting(
  targeting: MetaAudienceTargeting,
  opts: { reviewer: boolean; stored: MetaAudienceTargeting | null; availableNames: ReadonlySet<string> | null },
): { ok: true; targeting: MetaAudienceTargeting } | { ok: false; missingReasons: string[] } {
  const sentIds = new Set(targeting.excluded.map(a => a.id));
  const kept = opts.reviewer
    ? []
    : (opts.stored?.excluded ?? []).filter(a => !DEFAULT_EXCLUSIONS.includes(a.name) && !sentIds.has(a.id));
  const excluded = [...targeting.excluded, ...kept];
  const dropped = droppedDefaults(
    excluded.map(a => a.name),
    opts.availableNames,
  );
  const droppedExclusions = targeting.droppedExclusions.filter(d => dropped.includes(d.name));
  if (!opts.reviewer) {
    const missingReasons = dropped.filter(name => !droppedExclusions.some(d => d.name === name));
    if (missingReasons.length > 0) return { ok: false, missingReasons };
  }
  return { ok: true, targeting: { ...targeting, excluded, droppedExclusions } };
}
